import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  Box,
  Link as MuiLink,
} from '@mui/material';
import {
  People,
  Block,
  LinkedIn,
  CheckCircle,
} from '@mui/icons-material';
import { FcGoogle } from 'react-icons/fc';
import { Link as RouterLink } from 'react-router-dom';
import { StyledTableRow } from '../styled/TableComponents';
import { formatTimestamp } from '../../../utils/dateUtils';
import { getProxiedUrl, getOAuthProviders } from '../../../utils/userUtils';

const MicrosoftBrandLogo = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 23 23'
    xmlns='http://www.w3.org/2000/svg'
    aria-hidden='true'
  >
    <rect x='1'  y='1'  width='10' height='10' fill='#F25022' />
    <rect x='12' y='1'  width='10' height='10' fill='#7FBA00' />
    <rect x='1'  y='12' width='10' height='10' fill='#00A4EF' />
    <rect x='12' y='12' width='10' height='10' fill='#FFB900' />
  </svg>
);

const themeColors = {
  primary: '#232536',
  hover: '#3a3e5a',
};

const getUsername = (user) => {
  const candidate = user?.username ?? user?.userName;
  return typeof candidate === 'string' ? candidate.trim().replace(/^@+/, '') : '';
};

const UsersTable = ({
  users,
  loading,
  onStatusChange,
  onRoleChange,
  onUserClick,
}) => {
  if (loading) {
    return (
      <TableContainer component={Paper} sx={{ boxShadow: 1, borderRadius: 2 }}>
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Loading users...
          </Typography>
        </Box>
      </TableContainer>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ boxShadow: 1, borderRadius: 2 }}>
      <Table sx={{ tableLayout: 'fixed', minWidth: 1240 }}>
        <TableHead sx={{ bgcolor: '#f8fafc' }}>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, width: 250 }}>User</TableCell>
            <TableCell sx={{ fontWeight: 600, width: 170 }}>Username</TableCell>
            <TableCell sx={{ fontWeight: 600, width: 230 }}>Email</TableCell>
            <TableCell sx={{ fontWeight: 600, width: 90 }}>Is Admin</TableCell>
            <TableCell sx={{ fontWeight: 600, width: 90 }}>OAuth</TableCell>
            <TableCell sx={{ fontWeight: 600, width: 170 }}>Registered</TableCell>
            <TableCell sx={{ fontWeight: 600, width: 170 }}>Last Login</TableCell>
            <TableCell sx={{ fontWeight: 600, width: 100 }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 600, width: 120 }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} align="center">
                <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                  No users found
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            users.map((user) => {
              const username = getUsername(user);

              return (
                <StyledTableRow
                  key={user.email}
                  onClick={() => onUserClick?.(user)}
                  sx={{
                    cursor: onUserClick ? 'pointer' : 'default',
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar
                        src={getProxiedUrl(user.profilePictureUrl)}
                        alt={user.name}
                        imgProps={{
                          onError: (e) => {
                            e.target.src = '';
                          },
                          referrerPolicy: 'no-referrer',
                        }}
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: '#3b82f6',
                          fontWeight: 600,
                          fontSize: '1rem',
                        }}
                      >
                        {user.name?.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {user.name}
                        </Typography>
                        {user.company && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: 'block',
                            }}
                          >
                            {user.company}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {username ? (
                      <MuiLink
                        component={RouterLink}
                        to={`/@${encodeURIComponent(username)}`}
                        onClick={(event) => event.stopPropagation()}
                        sx={{
                          fontWeight: 500,
                          color: themeColors.primary,
                          textDecoration: 'none',
                          '&:hover': {
                            color: themeColors.hover,
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        @{username}
                      </MuiLink>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      {user.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {user.roles?.includes('ADMIN') ? (
                      <Tooltip title='Admin'>
                        <CheckCircle sx={{ color: '#16a34a', fontSize: 20 }} />
                      </Tooltip>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                      {Array.from(new Set(getOAuthProviders(user.oauth2Providers))).map(
                        (provider) => {
                          const normalizedProvider = (provider || '').toLowerCase();
                          const icon =
                            normalizedProvider === 'google' ? (
                              <FcGoogle size={16} />
                            ) : normalizedProvider === 'google-workspace' ? (
                              <FcGoogle size={16} />
                            ) : normalizedProvider === 'linkedin' ? (
                              <LinkedIn sx={{ fontSize: 16, color: '#0A66C2' }} />
                            ) : normalizedProvider === 'microsoft' ? (
                              <MicrosoftBrandLogo size={16} />
                            ) : null;

                          if (!icon) return null;

                          const providerLabel =
                            normalizedProvider === 'google'
                              ? 'Google'
                              : normalizedProvider === 'google-workspace'
                              ? 'Google Workspace'
                              : normalizedProvider === 'linkedin'
                              ? 'LinkedIn'
                              : normalizedProvider === 'microsoft'
                              ? 'Microsoft 365'
                              : provider;

                          return (
                            <Tooltip key={provider} title={`${providerLabel} Login`}>
                              <Box
                                sx={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: '50%',
                                  border: '1px solid #d1d5db',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  bgcolor: '#fff',
                                }}
                              >
                                {icon}
                              </Box>
                            </Tooltip>
                          );
                        }
                      )}
                      {(!user.oauth2Providers || user.oauth2Providers.length === 0) && (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {user.createdAt ? formatTimestamp(user.createdAt) : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {user.lastLogin ? formatTimestamp(user.lastLogin) : 'Never'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={user.isActive ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="Change Role">
                        <IconButton
                          size="small"
                          sx={{ color: '#3b82f6' }}
                          onClick={(event) => {
                            event.stopPropagation();
                            onRoleChange(user);
                          }}
                        >
                          <People />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={user.isActive ? 'Deactivate User' : 'Activate User'}
                      >
                        <IconButton
                          size="small"
                          sx={{
                            color: user.isActive ? '#ef4444' : '#10b981',
                          }}
                          onClick={(event) => {
                            event.stopPropagation();
                            onStatusChange(user.userId, user.isActive);
                          }}
                        >
                          <Block />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </StyledTableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default UsersTable;
